import { AuthenticationInfoHeader, LogoWrapper } from 'components/authentication/styled';
import CodeInput from 'components/code-input';
import React, { useEffect, useState } from 'react';
import EmailIcon from 'assets/svg/message.svg';
import { useLocale } from 'locale';
import { Link } from 'ds/components/link';
import { AppleAuthenticationInfo, AuthCodeWrapper, CodeInputWrapper, ControlsWrapper, ResendLinkWrapper, SkipLink, TimerText } from './styled';
import { Button } from 'ds/components/button';
import { SpacingBlock } from 'components/spacing-block';
import { AuthenticationModalType } from '../types';
import { EmailVerificationCodeStatus, IGenerateEmailVerificationCodeVariables, IUserAuthEventDataVariables, IVerifyEmailVerificationCodeVariables, MutationResponseByType, MutationType } from 'store/sagas/apiService/types';
import { ApolloQueryResult } from 'apollo-client';
import { noop } from 'lodash';
import Spinner from 'components/widget/prices/prices-table/tables/history/affiliation/Spinner';
import { State } from 'store/state';
import { userAuthEventDataSelector } from 'store/state/app/selectors';
import { connect } from 'react-redux';
import withEvents from 'analytics/withEvents';

const CODE_LENGTH = 4;
const START_TIME = 60;
const MIN_LOADING_DELAY = 500;

interface AuthCodeProps {
  email: string;
  setActiveModal: (type: AuthenticationModalType) => void;
  generateEmailVerificationCode: (data: IGenerateEmailVerificationCodeVariables) => void;
  verifyEmailVerificationCode: (data: IVerifyEmailVerificationCodeVariables) => void;
  verifyEmailVerificationCodeData: ApolloQueryResult<MutationResponseByType[MutationType.VerifyEmailVerificationCode]>;
  generateEmailVerificationCodeData: ApolloQueryResult<MutationResponseByType[MutationType.GenerateEmailVerificationCode]>;
  isUser: boolean;
  submitEventData?: IUserAuthEventDataVariables;
  onOpenModalEvent?: () => void;
}

const AuthenticationCodeBase: React.FC<AuthCodeProps> = ({
  email,
  setActiveModal,
  generateEmailVerificationCodeData,
  generateEmailVerificationCode,
  verifyEmailVerificationCode,
  verifyEmailVerificationCodeData,
  isUser,
  onOpenModalEvent = noop,
}) => {
  const { t } = useLocale();
  const [ code, setCode ] = useState('');
  const [ sendNewCode, setSendNewCode ] = useState(false);
  const [ time, setTime ] = useState(START_TIME);

  const isLoading = verifyEmailVerificationCodeData && verifyEmailVerificationCodeData.loading;
  const [ isDisabledBtn, setDisableBtn ] = useState(false);
  const hasData = !isLoading && verifyEmailVerificationCodeData && verifyEmailVerificationCodeData.data;
  const isSuccess = hasData && verifyEmailVerificationCodeData.data.verifyEmailVerificationCode.statusCode === EmailVerificationCodeStatus.SUCCESS;
  const isBlocked = generateEmailVerificationCodeData && generateEmailVerificationCodeData.data && generateEmailVerificationCodeData.data.generateEmailVerificationCode.statusCode === EmailVerificationCodeStatus.BLOCKED;
  const tries = hasData ? verifyEmailVerificationCodeData.data.verifyEmailVerificationCode.remainedRetries : 0;
  const dissableSubmit = !code || (code.length < CODE_LENGTH) || isDisabledBtn;
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (!sendNewCode && time > 0) {
      interval = setInterval(() => {
        setTime(time - 1);
      }, 1000);
    }
    else {
      clearInterval(interval);
      setSendNewCode(true);
    }
    return () => clearInterval(interval);
  }, [ sendNewCode, setTime, setSendNewCode, time ]);

  useEffect(() => {
    onOpenModalEvent();
  }, []);

  const handleBackToLogin = () => {
    setActiveModal(AuthenticationModalType.SignIn);
  };

  const handleResendCode = () => {
    setTime(START_TIME);
    setSendNewCode(false);
    generateEmailVerificationCode({ email });
  };

  const handleSendCode = () => {
    setDisableBtn(true);
    setTimeout(() => {
      verifyEmailVerificationCode({ email, code });
    }, MIN_LOADING_DELAY);
  };

  const showLoading = isLoading;

  return (
    <AuthCodeWrapper>
      <LogoWrapper>
        <EmailIcon width={64} height={64} />
      </LogoWrapper>
      <SpacingBlock mTop={4}>
        <AuthenticationInfoHeader weight="bold">
          {t('authentication.confirmEmail.title')}
        </AuthenticationInfoHeader>
      </SpacingBlock>
      <AppleAuthenticationInfo>{t('authentication.authCode.info', { email })}</AppleAuthenticationInfo>
      <CodeInputWrapper isSuccess={showLoading || !hasData || dissableSubmit ? null : isSuccess}>
        <CodeInput
          codeLength={CODE_LENGTH}
          value={code}
          onChange={() => setDisableBtn(false)}
          setValue={setCode}
          error={
            showLoading || !hasData
              ? false
              : (
                !isSuccess ? t('authentication.authError.verificationCode',  { tries }) : null
              )
          }
        />
      </CodeInputWrapper>
      <ControlsWrapper>
        <Button
          data-auto="authentication-submit-button"
          fullWidth
          size="extraLarge"
          disabled={dissableSubmit || showLoading || isSuccess}
          onClick={dissableSubmit || isLoading || isSuccess ? noop : handleSendCode}
          style={isLoading ? { lineHeight: 0 } : undefined}
        >
          {isLoading || isSuccess ? <Spinner /> : t('authentication.appleEmailConfirm.button')}
        </Button>
      </ControlsWrapper>
      {isBlocked ? null : (
        <ResendLinkWrapper>
          {sendNewCode
            ? <Link simple small style={{ cursor: 'pointer' }} onClick={handleResendCode}>{t('authentication.authCode.afterTimerLink')}</Link>
            : <TimerText>{t('authentication.authCode.timer', { time })}</TimerText>}
        </ResendLinkWrapper>
      )}
      {isUser ? null : (
        <>
          <SpacingBlock mTop={4} />
          <SkipLink>
            <Link simple onClick={handleBackToLogin}>{t('authentication.authCode.skipCode')}</Link>
          </SkipLink>
        </>
      )}
    </AuthCodeWrapper>
  );
};

const mapStateToProps = (state: State) => ({
  submitEventData: userAuthEventDataSelector(state),
});

export const AuthenticationCode = connect(mapStateToProps)(withEvents<AuthCodeProps>((sendEvent, { submitEventData: { source, signUpMethod, emailAddress } }) => ({
  verifyEmailVerificationCode: () => {
    sendEvent('user_sign_up_email_code_submit', 'user', {
      event: {
        'sign_up_method': signUpMethod,
        'email_address': emailAddress,
        source,
      },
    });
  },
  onOpenModalEvent: () => {
    sendEvent('popup_exposed', 'popup', {
      event: {
        type: 'email_verification_code',
        source: 'sign_up',
      },
    });
  },
}))(AuthenticationCodeBase));
